"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

interface ProductionSummary {
  batched: number;
  decorated: number;
  binned: number;
  total: number;
}

interface ProductionProgressProps {
  accountId?: string | null;
}

export function ProductionProgress({ accountId }: ProductionProgressProps) {
  const [summary, setSummary] = useState<ProductionSummary>({
    batched: 0,
    decorated: 0,
    binned: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProductionSummary();
  }, [accountId]);

  const fetchProductionSummary = async () => {
    setLoading(true);
    try {
      // Count items by status using Supabase count queries to avoid 1000 row limit
      const countByStatus = async (status: string): Promise<number> => {
        let query = supabase.from("order_items").select("*", { count: "exact", head: true }).eq("status", status);

        if (accountId) {
          query = query.eq("account_id", accountId);
        }

        const { count, error } = await query;

        if (error) {
          console.error(`Error counting ${status} items:`, error);
          return 0;
        }

        return count || 0;
      };

      // Fetch all counts in parallel
      const [batched, decorated, binned] = await Promise.all([
        countByStatus("batched"),
        countByStatus("decorated"),
        countByStatus("binned"),
      ]);

      const total = batched + decorated + binned;

      setSummary({
        batched,
        decorated,
        binned,
        total,
      });
    } catch (error) {
      console.error("Error fetching production summary:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base sm:text-lg">Production Progress</CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          {loading ? "Loading production data..." : `Summary of ${summary.total} items across all batches`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-2" />
            <span className="text-xs sm:text-sm text-slate-500">Loading production progress...</span>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 h-48 sm:h-64">
            {/* Cyan - Batched */}
            <div className="flex flex-col justify-end items-center">
              <div
                className="w-full bg-cyan-400 rounded-t-md transition-all duration-500 ease-out flex items-end justify-center pb-1 sm:pb-2"
                style={{
                  height: `${Math.max(
                    20,
                    (summary.batched /
                      Math.max(summary.batched, summary.decorated, summary.binned, 1)) *
                      100
                  )}%`,
                }}
              >
                <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">{summary.batched}</span>
              </div>
              <div className="w-full p-1 sm:p-2 bg-cyan-50 rounded-b-md text-center">
                <span className="text-xs sm:text-sm text-cyan-700 font-medium">Batched</span>
              </div>
            </div>
            {/* Magenta - Decorated */}
            <div className="flex flex-col justify-end items-center">
              <div
                className="w-full bg-fuchsia-400 rounded-t-md transition-all duration-500 ease-out flex items-end justify-center pb-1 sm:pb-2"
                style={{
                  height: `${Math.max(
                    20,
                    (summary.decorated /
                      Math.max(summary.batched, summary.decorated, summary.binned, 1)) *
                      100
                  )}%`,
                }}
              >
                <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">{summary.decorated}</span>
              </div>
              <div className="w-full p-1 sm:p-2 bg-fuchsia-50 rounded-b-md text-center">
                <span className="text-xs sm:text-sm text-fuchsia-700 font-medium">Decorated</span>
              </div>
            </div>
            {/* Yellow - Binned */}
            <div className="flex flex-col justify-end items-center">
              <div
                className="w-full bg-yellow-400 rounded-t-md transition-all duration-500 ease-out flex items-end justify-center pb-1 sm:pb-2"
                style={{
                  height: `${Math.max(
                    20,
                    (summary.binned /
                      Math.max(summary.batched, summary.decorated, summary.binned, 1)) *
                      100
                  )}%`,
                }}
              >
                <span className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">{summary.binned}</span>
              </div>
              <div className="w-full p-1 sm:p-2 bg-yellow-50 rounded-b-md text-center">
                <span className="text-xs sm:text-sm text-yellow-700 font-medium">Binned</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
